import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast, Toaster } from "sonner";
import { submitContact } from "@/lib/apiClient";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";

const SERVICE_OPTIONS = [
  "Database Management", "Data Pipelines", "Power BI", "Tableau",
  "Qlik", "Sisense", "MicroStrategy", "Zoho Analytics",
  "Snowflake Architecture", "MS SQL / Oracle", "Other",
];
const INDUSTRY_OPTIONS = [
  "Financial Services", "Manufacturing", "Healthcare", "Retail",
  "Energy & Utilities", "Public Sector", "Logistics", "SaaS & Tech", "Other",
];
const BUDGET_OPTIONS = ["< $50k", "$50k – $150k", "$150k – $500k", "$500k – $1M", "$1M+"];
const TIMELINE_OPTIONS = ["Immediate (< 30 days)", "30–60 days", "60–90 days", "Exploring"];

const Field = ({ label, children, testId }) => (
  <div className="flex flex-col gap-2" data-testid={`field-${testId}`}>
    <Label className="text-xs uppercase tracking-widest text-white/50">{label}</Label>
    {children}
  </div>
);

const Contact = () => {
  const [form, setForm] = useState({
    name: "", email: "", company: "", role: "", phone: "",
    industry: "", service_interest: "", budget: "", timeline: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v?.target?.value ?? "" }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.message) {
      toast.error("Please complete name, email, company and message.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success("Submission received. We'll be in touch within 24 hours.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toaster theme="dark" position="top-right" richColors />
      <section className="bg-[#0a0a0a] text-white grain relative overflow-hidden" data-testid="contact-hero">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-[#0055FF]/15 blur-[180px] rounded-full" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 pb-12 relative grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-7">
            <p className="text-xs tracking-[0.25em] uppercase text-white/40 mb-6">Contact</p>
            <h1 className="font-display text-[clamp(2.5rem,7vw,6.5rem)] leading-[0.9] tracking-tighter">
              Tell us what <br />you're trying <br />
              to <span className="text-[#0055FF]">measure.</span>
            </h1>
          </div>
          <div className="col-span-12 md:col-span-5 md:pl-8 md:border-l md:border-white/10 self-end">
            <p className="text-white/70 leading-relaxed">
              Senior architects respond within one business day with a
              tailored scoping document and indicative range.
            </p>
            <div className="mt-6 text-sm text-white/50">
              <p>info@pragmaanalyticsgroup.com</p>
              <p>+1 (281) 305-8509</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#0a0a0a] text-white" data-testid="contact-form-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24 pt-8 grid grid-cols-12 gap-10">
          <aside className="col-span-12 md:col-span-4">
            <div className="border-t border-white/10 pt-6">
              <p className="text-xs uppercase tracking-widest text-white/40 mb-3">What happens next</p>
              <ul className="space-y-5 mt-6">
                {[
                  "Architect review within 1 business day",
                  "30-minute scoping call (no sales pitch)",
                  "Written proposal with fixed deliverables",
                  "Kick-off within 7 days of contract sign",
                ].map((s, i) => (
                  <li key={s} className="flex gap-3 items-start">
                    <span className="font-display text-sm text-[#0055FF] mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm text-white/70 leading-relaxed">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <form className="col-span-12 md:col-span-8 border-t border-white/10 pt-8" onSubmit={onSubmit} data-testid="contact-form">
            {done ? (
              <div className="border border-[#0055FF]/40 bg-[#0055FF]/10 p-10 flex flex-col items-start gap-4" data-testid="contact-success">
                <CheckCircle2 className="w-10 h-10 text-[#0055FF]" />
                <h3 className="font-display text-3xl tracking-tighter">We received your brief.</h3>
                <p className="text-white/70 max-w-md">
                  A senior architect will reach out within one business day to schedule a 30-minute scoping call.
                </p>
                <Button
                  type="button"
                  onClick={() => { setDone(false); setForm({ name: "", email: "", company: "", role: "", phone: "", industry: "", service_interest: "", budget: "", timeline: "", message: "" }); }}
                  className="bg-white hover:bg-zinc-200 text-black rounded-none h-11 px-6 mt-2"
                  data-testid="contact-success-reset"
                >
                  Submit another inquiry
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Full name *" testId="name">
                  <Input data-testid="input-name" value={form.name} onChange={set("name")} className="rounded-none bg-transparent border-white/20 text-white h-12 focus-visible:ring-[#0055FF] focus-visible:border-[#0055FF]" placeholder="Jane Doe" />
                </Field>
                <Field label="Work email *" testId="email">
                  <Input data-testid="input-email" type="email" value={form.email} onChange={set("email")} className="rounded-none bg-transparent border-white/20 text-white h-12 focus-visible:ring-[#0055FF] focus-visible:border-[#0055FF]" placeholder="jane@company.com" />
                </Field>
                <Field label="Company *" testId="company">
                  <Input data-testid="input-company" value={form.company} onChange={set("company")} className="rounded-none bg-transparent border-white/20 text-white h-12 focus-visible:ring-[#0055FF] focus-visible:border-[#0055FF]" placeholder="Company Inc." />
                </Field>
                <Field label="Role" testId="role">
                  <Input data-testid="input-role" value={form.role} onChange={set("role")} className="rounded-none bg-transparent border-white/20 text-white h-12 focus-visible:ring-[#0055FF] focus-visible:border-[#0055FF]" placeholder="VP Analytics" />
                </Field>
                <Field label="Phone" testId="phone">
                  <Input data-testid="input-phone" value={form.phone} onChange={set("phone")} className="rounded-none bg-transparent border-white/20 text-white h-12 focus-visible:ring-[#0055FF] focus-visible:border-[#0055FF]" placeholder="+1 415 555 0117" />
                </Field>
                <Field label="Industry" testId="industry">
                  <Select value={form.industry} onValueChange={set("industry")}>
                    <SelectTrigger data-testid="select-industry" className="rounded-none bg-transparent border-white/20 text-white h-12">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[#0a0a0a] border-white/20 text-white">
                      {INDUSTRY_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Primary service interest" testId="service-interest">
                  <Select value={form.service_interest} onValueChange={set("service_interest")}>
                    <SelectTrigger data-testid="select-service" className="rounded-none bg-transparent border-white/20 text-white h-12">
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[#0a0a0a] border-white/20 text-white">
                      {SERVICE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Budget range" testId="budget">
                  <Select value={form.budget} onValueChange={set("budget")}>
                    <SelectTrigger data-testid="select-budget" className="rounded-none bg-transparent border-white/20 text-white h-12">
                      <SelectValue placeholder="Indicative budget" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[#0a0a0a] border-white/20 text-white">
                      {BUDGET_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Timeline" testId="timeline">
                  <Select value={form.timeline} onValueChange={set("timeline")}>
                    <SelectTrigger data-testid="select-timeline" className="rounded-none bg-transparent border-white/20 text-white h-12">
                      <SelectValue placeholder="When would you start?" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[#0a0a0a] border-white/20 text-white">
                      {TIMELINE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="md:col-span-2">
                  <Field label="Project brief *" testId="message">
                    <Textarea data-testid="input-message" value={form.message} onChange={set("message")} rows={6} className="rounded-none bg-transparent border-white/20 text-white focus-visible:ring-[#0055FF] focus-visible:border-[#0055FF]" placeholder="Describe the business outcome you want to drive, current stack, and known constraints." />
                  </Field>
                </div>
                <div className="md:col-span-2 mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <p className="text-xs text-white/40 max-w-md">
                    By submitting, you agree to be contacted by Pragma Analytics Group regarding your inquiry.
                  </p>
                  <Button type="submit" disabled={loading} data-testid="contact-submit" className="bg-[#0055FF] hover:bg-[#0044cc] text-white rounded-none h-12 px-8 font-medium tracking-tight disabled:opacity-60">
                    {loading ? "Sending…" : (<>Send brief <ArrowUpRight className="w-4 h-4 ml-1" /></>)}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>
      </section>
    </>
  );
};

export default Contact;
